import moment from "moment-timezone";
import { TILE_STATES } from '../../../components/cap/constants';
import {
    generateSKU,
    getCompanyId,
    getProjectsFromCookie,
    isAlternateCID,
    isInventoryCheckRequired
} from '../../../components/global/utils/commonUtils';
import { isValidString, logError } from '../../../components/global/utils/logger';
import { ENV_CONFIG } from '../../../constants/envConfig';
import { ERROR_MSG } from '../../../constants/errorMappingCodes';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import { USER_TYPE } from '../../../constants/userDetailsConstants';
import { useCheckUser } from '../../../hooks/useCheckUser';
import { useAuthorityToken } from '../../utils/hooks';
import axiosInstance from '../../../api/axiosDotComInstance';
import parseError from '../../utils/parseError';
import isObjectWithKeys from "../../utils/isObjectWithKeys";
import {
    RESET_GEOGRAPHY,
    RESET_ORDER_ESTIMATES,
    SET_CART_AVAILABLE_UNVAILABLE_ITEMS,
    SET_CART_ITEMS,
    SET_IS_CART_LOADING
} from '../constants';
import { generateLocalCartItems, updateLocalCartItems, formattedCartDetailsWithEstimates } from './utils';
import { getRentablesPrice } from '../../../components/checkoutv2/checkoutAndOrderSummary/api/getCheckoutAPIs';
import { LINE_ITEM_TYPE_SALES } from "../../../components/molecules/orderSummaryFactory/constants/orderSummaryFactoryConstants";
import { API_CONFIG } from "../../../constants/apiConfig";

/**
 * Adds an item to the cart. If the cart doesn't exist then it's created
 *
 * @param {Object} payload a parameters object with the following structure:
 *      cartDetailsQuery - the query object to execute
 *      createCartMutation - the mutation to execute to create the cart
 *      addToCartMutation - the mutation to execute to add the item to the cart
 *      dispatch - the dispatch callback for the cart context
 *      cartItems - the items to add to the cart
 *      cartId - the id of the cart in which to add the items. This could be `undefined` or `null`
 */

 export const addItemToCart = async payload => {
    const { createCartMutation, addToCartMutation, physicalCartItems, virtualCartItems, bundleCartItems, accessories = '', dispatch } =
        payload;

    try {
        const formattedDate = moment().format('YYYY-MM-DD HH:mm:ss');
        let cartId = payload?.cartId;
        if (!cartId) {
            let { data: newCartData } = await createCartMutation();
            cartId = newCartData?.createEmptyCart;
        }
        let variables = { cartId, cartItems: physicalCartItems };
        if(accessories) {
            variables.accessories = accessories;
        }
        if (bundleCartItems?.length > 0) {
            variables = { cartId, cartItems: bundleCartItems };
        } else if (physicalCartItems?.length > 0 && virtualCartItems?.length > 0) {
            variables = { cartId, virtualCartItems, simpleCartItems: physicalCartItems };
        } else if (virtualCartItems?.length > 0) {
            variables = { cartId, cartItems: virtualCartItems };
        }
        const res = await addToCartMutation({ variables });
        dispatch({ type: 'cartId', cartId });
        const userProfileStorage = JSON.parse(sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.USERPROFILE) || '{}');
        if (isObjectWithKeys(userProfileStorage)) {
            // for cancel editing quote cart, the cart id in storage will be empty so on page redirect, 
            // the cartId will be set as empty to prevent that on creating new cart, updating that in storage
            userProfileStorage.cart_token = cartId;
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.USERPROFILE, JSON.stringify(userProfileStorage));
        }
        const atcData = {
            cartId,
            created_at: res?.data?.addSimpleProductsToCart?.cart?.created_at,
            updated_at: formattedDate,
            total_quantity: res?.data?.addSimpleProductsToCart?.cart?.total_quantity || 0
        };
        dispatch({
            type: SET_CART_ITEMS,
            id: cartId,
            created_at: atcData?.created_at,
            updated_at: atcData?.updated_at,
            total_quantity: atcData?.total_quantity
        });
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY, atcData?.total_quantity);
        const atcItems = res?.data?.addSimpleProductsToCart?.cart?.items || [];
        const uid = atcItems?.find(item => item?.sku === variables?.cartItems?.[0]?.data?.sku)?.uid;
        updateLocalCartItems({
            id: variables?.cartItems?.[0]?.data?.sku,
            quantity: variables?.cartItems?.[0]?.data?.quantity,
            uid
        });
        return { atcData, uid, error: '' };
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'addItemToCart', [payload]);
        } else {
            logError(error, false, 'addItemToCart', [payload]);
        }
        return { atcData: {}, error };
    }
};

/**
 * Retrieves the cart details
 * @param {Object} payload a parameters object with the following structure:
 *      cartDetailsQuery - the query object to execute
 *      dispatch - the dispatch callback for the cart context
 *      cartId - the id of the cart
 */
//commenting unused function
// const getRequiredPCsForInventory = (startDateAndTime, nearbyPcs) => {
//     const NewAddress = localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ISCREDITNEWADDRESS)
//         ? JSON.parse(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ISCREDITNEWADDRESS))
//         : false;
//     const pcList = nearbyPcs
//         ? JSON.stringify(nearbyPcs)
//         : sessionStorage.getItem(STORAGE_CONFIG.SESSION_STORAGE.ATP_PC_LIST);
//     const projectInfoCookies = getProjectsFromCookie();
//     const userType = useCheckUser();
//     const { disableinventorycheck, rollbackToCi } = ENV_CONFIG.INVENTORY_CHECK_CONFIGS || {};
//     const isInventoryCheckDisabled = disableinventorycheck || false;
//     const isAtpDisabled = rollbackToCi || false;
//     if (isValidString(isInventoryCheckDisabled)) {
//         return '';
//     } else if (!NewAddress && !isValidString(projectInfoCookies?.SLA) && userType == USER_TYPE.CREDIT) {
//         return '';
//     } else if (isInventoryCheckRequired() && pcList) {
//         if (!isValidString(isAtpDisabled)) {
//             if (!isValidString(startDateAndTime)) {
//                 return JSON.parse(pcList?.replaceAll('01_', '')?.replaceAll('02_', ''));
//             }
//             return '';
//         }
//         return JSON.parse(pcList?.replaceAll('01_', '')?.replaceAll('02_', ''));
//     } else {
//         return '';
//     }
// };
export const getCartDetails = async payload => {
    const {
        cartDetailsQuery,
        dispatch,
        cartId,
        filterDispatch,
        handleATPCart,
        ...rest
    } = payload;
    const {
        pc,
        dlSt,
        dlNumber,
        distance,
        timeType,
        equipment,
        salesEquipments,
        jobSiteAddr1,
        jobSiteAddr2,
        lat,
        long,
        jobSiteCity,
        jobSiteState,
        jobSiteZip,
        startDateAndTime,
        endDateAndTime,
        lastResort,
        fulfillment,
        pcLat,
        pcLong,
        projectId,
        account,
        isDeliveryEstimate,
        signal
    } = rest;
    try {
        if (!cartId) {
            return;
        }
        const context = {
            headers: {
                    'company-id': getCompanyId(),
                    'required-pcs': ''
                }
        };
        if (signal) {
            context.fetchOptions =  { signal };
        }

        const cartInputDataObj = JSON.stringify({
            pc,
            jobSiteAddr1,
            jobSiteAddr2,
            jobSiteCity,
            jobSiteState,
            jobSiteZip,
            dlSt,
            dlNumber,
            startDateAndTime,
            endDateAndTime,
            lat,
            long,
            distance,
            timeType,
            equipment,
            salesEquipments,
            lastResort,
            fulfillment,
            pcLat,
            pcLong,
            projectId,
            account,
            isDeliveryEstimate,
        });
        const { data, errors } = await cartDetailsQuery({
            variables: {
                cartId,
                cartInputData: cartInputDataObj !== "{}"
                                ? cartInputDataObj
                                : undefined,
            },
            fetchPolicy: 'network-only',
            context
        });
        //TODO- make wires crossed as true
        if (errors) {
            logError(errors[0]?.message || ERROR_MSG?.INTERNAL_SERVER_ERROR, false, 'getCartDetails', [payload]);
        }
        let estimatesData = structuredClone(data.cart);
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY, estimatesData?.total_quantity || 0);
        generateLocalCartItems(estimatesData?.items);
        dispatch({ type: 'cart', cart: estimatesData });
        return estimatesData;
    } catch (error) {
        logError(error, false, 'getCartDetails', [payload]);
        dispatch({ type: 'error', error: error.toString() });
        return {};
    }
};

export const updateEstimates = async (cartResponse, cart, payload, dispatch) =>{
    const {
        cartId,
        isRentalDetailsAvailable = true,
        ...rest
    } = payload;
    try {
        const userType = useCheckUser();
        const projectInfoCookies = getProjectsFromCookie();
        const isCreditNewAddressFromStorage =JSON.parse(localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ISCREDITNEWADDRESS) || false);
        if (!cartId) {
            return;
        }
        const { data, error } = await getRentablesPrice(payload);
        let estimatesData = structuredClone(data);

        const cartObj = !cart ? cartResponse : cart;
        const { items, prices } = formattedCartDetailsWithEstimates(cartObj, data?.estimate);
        estimatesData = { 
            ...estimatesData, 
            items, 
            prices 
        };
        const chargedItems = { ...estimatesData?.estimate?.itemizedCharges?.products };
        let availableCartItems = [];
        let unavailableCartItems = [];
        let estimateCartItems = [];

        if (!isRentalDetailsAvailable || error) {
            const cartItems = estimatesData?.items;
            for (let i in cartItems) {
                estimateCartItems.push({
                    ...cartItems[i],
                    product: {
                        ...cartItems[i].product,
                        pc_availability: true
                    }
                });
                availableCartItems.push({
                    ...cartItems[i],
                    product: {
                        ...cartItems[i].product,
                        pc_availability: true
                    }
                });
            }
        } else {
            iterateCartItems: for (let i in estimatesData?.items) {
                for (let j in chargedItems) {
                    if (
                        generateSKU(chargedItems[j]?.catId, chargedItems[j]?.classId) ===
                        estimatesData?.items[i]?.product?.sku
                    ) {
                        estimateCartItems.push({
                            ...estimatesData.items[i],
                            product: {
                                ...estimatesData.items[i].product,
                                pc_availability: true
                            }
                        });
                        availableCartItems.push({
                            ...estimatesData.items[i],
                            product: {
                                ...estimatesData.items[i].product,
                                pc_availability: true
                            }
                        });
                        continue iterateCartItems;
                    }
                }
                estimateCartItems.push({
                    ...estimatesData.items[i],
                    product: {
                        ...estimatesData.items[i].product,
                        pc_availability: false
                    }
                });
                unavailableCartItems.push({
                    ...estimatesData.items[i],
                    product: {
                        ...estimatesData.items[i].product,
                        pc_availability: false
                    }
                });
            }
        }

        estimatesData.items = estimateCartItems;
        const isInstorePickupFromStorage = JSON.parse(
            localStorage.getItem(STORAGE_CONFIG.LOCAL_STORAGE.ISINSTOREPICKUP) || false
        );

        /**
         * Response types on the basis of timeType
         *
         * timeType:pickup => cart.estimatesResponse.estimate => pickup
         * timeType:delivery => cart.estimatesResponse.estimate => delivery
         * timeType:delivery-pickup => cart.estimatesResponse.estimate => delivery
         * timeType:delivery-pickup => cart.estimatesResponse.estimatePickup => pickup
         */

        if (estimatesData?.estimate) {
            const estimatesDefaultTotals = modifyEstimates(estimatesData.estimate);
            const estimatesDefault = {
                ...estimatesData.estimate,
                totals: {
                    ...estimatesData.estimate.totals,
                    fuelCharges: estimatesDefaultTotals?.fuelCharges,
                    deliveryPickUpCharges: estimatesDefaultTotals?.deliveryPickUpCharges,
                    allOtherCharges: estimatesDefaultTotals?.allOtherCharges || 0
                }
            };
            if (estimatesData?.estimatePickup) {
                // if timeType is delivery-pickup
                // in case of delivery-pickup we are keeping both estimates in session storage
                const estimatesPickupTotals = modifyEstimates(estimatesData.estimatePickup);
    
                const estimatesPickup = {
                    ...estimatesData.estimatePickup,
                    totals: {
                        ...estimatesData.estimatePickup.totals,
                        fuelCharges: estimatesPickupTotals?.fuelCharges,
                        deliveryPickUpCharges: estimatesPickupTotals?.deliveryPickUpCharges,
                        allOtherCharges: estimatesPickupTotals?.allOtherCharges || 0
                    }
                };

                const estimates = isInstorePickupFromStorage ? estimatesPickup : estimatesDefault;
                
                estimatesData = {
                    ...cartObj,
                    estimatesResponse: {
                        estimate: estimates
                    },
                    items: estimateCartItems
                };
                sessionStorage.setItem(
                    STORAGE_CONFIG.SESSION_STORAGE.ESTIMATES_PICKUP,
                    JSON.stringify(estimatesPickup)
                );
                sessionStorage.setItem(
                    STORAGE_CONFIG.SESSION_STORAGE.ESTIMATES_DELIVERY,
                    JSON.stringify(estimatesDefault)
                );
            } else {
                // if timeType is delivery or pickup
                estimatesData = {
                    ...cartObj,
                    estimatesResponse: {
                        estimate: estimatesDefault
                    },
                    items: estimateCartItems
                };
            }
        } 
        generateLocalCartItems(availableCartItems);
        estimatesData = { ...estimatesData, availableCartItems, unavailableCartItems };
        if (userType === USER_TYPE.CREDIT && !isCreditNewAddressFromStorage && !isAlternateCID()) {
            if (!projectInfoCookies?.SLA) {
                estimatesData = {
                    ...estimatesData,
                    prices: {},
                    estimatesResponse: {}
                };
                filterDispatch({
                    type: RESET_GEOGRAPHY
                });
                dispatch({
                    type: RESET_ORDER_ESTIMATES
                });
            }
        }
        dispatch({ type: 'cart', cart: estimatesData });
        return estimatesData;
    } catch (error) {
        logError(error, false, 'updateEstimates', [payload]);
        dispatch({ type: 'error', error: error.toString() });
        return {};
    }
}

const getForcedFuelCharges = (miscCharges) => {
    const validFuelForcedItems = JSON.parse(sessionStorage.getItem('validFuelForcedItems'));
    const fuelCharges = miscCharges
                        ?.filter(misc => validFuelForcedItems?.some(fuel => fuel?.itemDescription?.toUpperCase() === misc?.type?.toUpperCase()))
                        ?.reduce((total, misc) => total + misc?.charge, 0);
    return fuelCharges || 0;
}

const modifyEstimates = estimates => {
    if (estimates) {
        const deliveryObj = estimates?.miscCharges?.find(item => item.type.indexOf('DELIVERY') > -1);
        const pickupObj = estimates?.miscCharges?.find(item => item.type.indexOf('PICKUP') > -1);
        const fuelObj = estimates?.miscCharges?.find(item => item.type.indexOf('FUEL') > -1 && !item.type.includes('FUEL/')); // adding this condition because we have noticed type: "FILTER, MICROLITE PRIMARY FUEL/WATER C" which is not an actual fuel item
        const salesItemCharges = estimates?.miscCharges?.reduce((total, item) => {
            return item?.lineItemType?.toUpperCase() === LINE_ITEM_TYPE_SALES ? total + item?.charge : total;
        }, 0); //these are forced addons in misdCharges
        const forcedMiscFuelCharges = getForcedFuelCharges(estimates?.miscCharges)

        const fuelCharges = fuelObj?.charge ? fuelObj?.charge : 0;
        const deliveryCharge = deliveryObj?.charge ? deliveryObj?.charge : 0;
        const pickupCharge = pickupObj?.charge ? pickupObj?.charge : 0;
        const deliveryPickUpCharges = deliveryCharge + pickupCharge;
        const allOtherCharges = estimates?.totals?.miscCharges - deliveryPickUpCharges - salesItemCharges - forcedMiscFuelCharges;

        return { fuelCharges, deliveryPickUpCharges, allOtherCharges, salesItemCharges };
    }
};

/**
 * Removes an item from the cart
 *
 * @param {Object} payload a parameters object with the following structure:
 *      cart - cart data
 *      removeItemMutation - the mutation to execute to remove the items
 *      dispatch - the dispatch callback for the cart context
 *      cartId - the id of the cart
 *      itemId - the id of the item to remove
 */
export const removeItemFromCart = async payload => {
    const { cart, removeItemMutation, cartId, itemUid, dispatch } = payload;
    try {
        const res = await removeItemMutation({
            variables: { cartId, itemUid }
        });

        const updatedItems = cart?.items?.filter(item => item?.uid !== itemUid);
        dispatch({
            type: SET_CART_ITEMS,
            id: cartId,
            items: updatedItems,
            total_quantity: res?.data?.removeItemFromCart?.cart?.total_quantity
        });
        localStorage.setItem(
            STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY,
            res?.data?.removeItemFromCart?.cart?.total_quantity || 0
        );
        generateLocalCartItems(updatedItems);
        return updatedItems;
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'removeItemFromCart', [payload]);
        } else {
            logError(error, false, 'removeItemFromCart', [payload]);
        }
        dispatch({ type: 'error', error: error?.toString() });
    }
};

/**
 * Merges two shopping carts
 *
 * @param {Object} payload a parameters object with the following structure:
 *      cartDetailsQuery - the query object to execute
 *      mergeCartsMutation - the mutation to execute to merge the carts
 *      dispatch - the dispatch callback for the cart context
 *      cartId - the id of the cart to be merged
 *      customerCartId - the id of the cart that the other cart will the merged into (i.e. the customer cart)
 */
export const mergeCarts = async payload => {
    const { cartDetailsQuery, mergeCartsMutation, cartId, customerCartId, dispatch, filterDispatch } = payload;
    try {
        const { data } = await mergeCartsMutation({
            variables: {
                sourceCartId: cartId,
                destinationCartId: customerCartId
            }
        });
        await getCartDetails({ cartDetailsQuery, dispatch, cartId: data.mergeCarts.id, filterDispatch });
        return data.mergeCarts.id;
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'mergeCarts', [payload]);
        } else {
            logError(error, false, 'mergeCarts', [payload]);
        }
        dispatch({ type: 'error', error: parseError(error) });
    }
};

const updateCartAvailableUnavailableItems = (items, handleATPCart, dispatch, totalQty) => {
    try {
        let availableCartItems = [];
        let unavailableCartItems = [];
        if (items?.length > 0) {
            items?.forEach(data => {
                let tileStatus = handleATPCart({
                    catsku: data?.product?.sku,
                    inventoryDataObj: data?.product?.ec_pc_inventory
                });
                if (tileStatus === TILE_STATES.AVAILABLE || tileStatus === TILE_STATES.AVAILABLE_WITH_WARNING) {
                    availableCartItems.push(data);
                } else {
                    unavailableCartItems.push(data);
                }
            });
        }
        dispatch({ type: SET_CART_AVAILABLE_UNVAILABLE_ITEMS, availableCartItems, unavailableCartItems });
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY, totalQty || 0);
        generateLocalCartItems(items);
        if (!totalQty) {
            dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
        }
    } catch (error) {
        logError(error, false, 'updateCartAvailableUnavailableItems');
    }
};

export const removeMultipleUnavailableItems = async payload => {
    const { updateCartItemMutation, cartId, handleATPCart, dispatch, cartItems, cart = {} } = payload || {};
    try {
        const res = await updateCartItemMutation({
            variables: { cartId, cartItems }
        });

        const responseItems = res?.data?.updateCartItems?.cart?.items || [];
        const items = cart?.items?.filter(cartItem => responseItems?.find(resItem => resItem?.uid === cartItem?.uid));

        dispatch({
            type: SET_CART_ITEMS,
            id: cartId,
            items: items,
            total_quantity: res?.data?.updateCartItems?.cart?.total_quantity
        });
        /* To update the cart contex when unavailable items is removed form the cart */
        updateCartAvailableUnavailableItems(
            items,
            handleATPCart,
            dispatch,
            res?.data?.updateCartItems?.cart?.total_quantity
        );
        return items;
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'removeMultipleUnavailableItems', [payload]);
        } else {
            logError(error, false, 'removeMultipleUnavailableItems', [payload]);
        }
        dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
        dispatch({ type: 'error', error: parseError(error) });
    }
};

export const updateCartItem = async payload => {
    const { updateCartItemMutation, cartId, handleATPCart, dispatch, cartItems, cart = {} } = payload || {};
    try {
        const res = await updateCartItemMutation({
            variables: { cartId, cartItems }
        });

        const responseItems = res?.data?.updateCartItems?.cart?.items || [];
        const items = cart?.items?.map(cartItem => {
            const matchingCartItem = responseItems?.find(resItem => resItem?.uid === cartItem?.uid);
            if (matchingCartItem) {
                return { ...cartItem, quantity: matchingCartItem?.quantity }; // Update the quantity
            }
            return cartItem; // Return the item unchanged if no match
        });

        dispatch({
            type: SET_CART_ITEMS,
            id: cartId,
            items: items,
            total_quantity: res?.data?.updateCartItems?.cart?.total_quantity
        });
        /* To update the cart contex when unavailable items is removed form the cart */
        updateCartAvailableUnavailableItems(
            items,
            handleATPCart,
            dispatch,
            res?.data?.updateCartItems?.cart?.total_quantity
        );
        return items;
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'updateCartItem', [payload]);
        } else {
            logError(error, false, 'updateCartItem', [payload]);
        }
        dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
        dispatch({ type: 'error', error: parseError(error) });
    }
};

export const updateLocalCartItem = async payload => {
    const { updateCartItemMutation, cartId, dispatch, cartItems, productId } = payload || {};
    try {
        const res = await updateCartItemMutation({
            variables: { cartId, cartItems }
        });
        const total_quantity = res?.data?.updateCartItems?.cart?.total_quantity || 0;
        dispatch({
            type: SET_CART_ITEMS,
            id: cartId,
            total_quantity
        });
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY, total_quantity);
        updateLocalCartItems({
            id: productId,
            uid: cartItems?.[0]?.cart_item_uid,
            quantity: cartItems?.[0]?.quantity,
        }, true);
    } catch (error) {
        if (error?.response?.status >= 500) {
            logError(error, true, 'updateLocalCartItem', [payload]);
        } else {
            logError(error, false, 'updateLocalCartItem', [payload]);
        }
        dispatch({ type: 'error', error: parseError(error) });
    }
};

export const replaceCartItemWithSimilarItem = async payload => {
    const { replaceCartItemMutation, cartId, handleATPCart, dispatch, cart = {},  unavailableItemSku, similarItemsData} = payload || {};        
    try {
        const response = await replaceCartItemMutation({
            variables: { cartId, unavailableItemSku, similarItemSku: similarItemsData?.catsku }
        });
            const responseItems = response?.data?.replaceUnavailableSku?.items || [];
            const totalQty = response?.data?.replaceUnavailableSku?.total_quantity || 0;
            const cartItems = cart?.items || [];
            
            // Create a Set of uids from cartItems for fast lookup
            const cartUidSet = new Set(cartItems?.map(item => item?.uid));

            // Remove items from cartItems whose uid is not in responseItems
            const updatedCartItems = cartItems?.filter(item => 
                responseItems?.some(responseItem => responseItem?.uid === item?.uid)
            );

            // Add items from responseItems that are not in cartUidSet or update existing ones
            responseItems?.forEach(responseItem => {
                const existingItemIndex = updatedCartItems.findIndex(item => item?.uid === responseItem?.uid);

                if (!cartUidSet?.has(responseItem?.uid)) {
                    // If the uid is not in cartUidSet, add the item
                    updatedCartItems.push({
                        uid: responseItem?.uid,
                        quantity: responseItem?.quantity,
                        product: {
                            name: responseItem?.name,
                            sku: responseItem?.sku,
                            product_page_url: similarItemsData?.producturl,
                            ec_pc_inventory: similarItemsData?.inventory,
                            category_name: similarItemsData?.categoryname,
                            parent_category_name: similarItemsData?.productcategoryname,
                            category_path: similarItemsData?.category_path,
                            disableaddtocartoption: similarItemsData?.disableaddtocartoption,
                            showonlinecatalog: similarItemsData?.showonlinecatalog,
                            greenleafdata: responseItem?.product?.greenleafdata,
                            isgreenleaf: responseItem?.product?.isgreenleaf,
                            thumbnail:  responseItem?.product?.thumbnail,
                            pc_availability: similarItemsData?.pc_availability,
                        }
                    });
                } else if (existingItemIndex > -1) {
                    // If the uid is in cartUidSet, replace the item in updatedCartItems
                    updatedCartItems[existingItemIndex] = {
                        quantity: responseItem?.quantity,
                        ...updatedCartItems[existingItemIndex],
                    };
                }
            });
            dispatch({
                type: SET_CART_ITEMS,
                id: cartId,
                items: updatedCartItems,
                total_quantity: totalQty
            });
            /* To update the cart contex when unavailable items is removed form the cart */
            updateCartAvailableUnavailableItems(
                updatedCartItems,
                handleATPCart,
                dispatch,
                totalQty
            );
            return updatedCartItems;
    } catch (error) {
        logError(error, false, 'replaceCartItemWithSimilarItem', [payload]);
        dispatch({ type: SET_IS_CART_LOADING, isCartLoading: false });
        dispatch({ type: 'error', error: parseError(error) });
    }
};

export const createReservationProcess = async props => {
    const { createReservationMutation, payload, dispatch, quoteId } = props;
    try {
        let { data, error } = await createReservationMutation({
            variables: payload,
            context: {
                headers: {
                    ...(quoteId && { convertquoteid: quoteId })
                }
            }
        });
        if (error) {
            logError(error, false, 'createReservationProcess', [props]);
        }
        dispatch({ type: 'reservation', data: data });
        return { data, error };
    } catch (error) {
        dispatch({ type: 'error', error: error.toString() });
        if (error?.response?.status >= 500) {
            logError(error, true, 'createReservationProcess', [props]);
        } else {
            logError(error, false, 'createReservationProcess', [props]);
        }
    }
};

export const createQuoteProcess = async props => {
    const { ecomCreateQuote, payload, dispatch } = props;
    try {
        let { data, error } = await ecomCreateQuote({ variables: payload });
        if (error) {
            logError(error, false, 'createQuoteProcess', [props]);
        }
        dispatch({ type: 'reservation', data: data });
        return { data, error };
    } catch (error) {
        dispatch({ type: 'error', error: error.toString() });
        if (error?.response?.status >= 500) {
            logError(error, true, 'createQuoteProcess', [props]);
        } else {
            logError(error, false, 'createQuoteProcess', [props]);
        }
    }
};


export const createOrTransmitQuotePayloadProcess = async props => {
    const { createOrTransmitQuoteMutation, payload, dispatch } = props;
    try {
        let { data, error } = await createOrTransmitQuoteMutation({ variables: payload });
        return { data: data?.createQuote, error };
    } catch (error) {
        logError(error, false, 'createOrTransmitQuotePayloadProcess', [props]);
        return { data: null, error };
    }
};

export const editOrSaveQuotePayloadProcess = async props => {
    const { editOrSaveQuoteMutation, payload, dispatch } = props;
    try {
        let { data, error } = await editOrSaveQuoteMutation({ variables: payload });
        return { data: data?.editQuote, error };
    } catch (error) {
        logError(error, false, 'editOrSaveQuotePayloadProcess', [props]);
        return { data: null, error };
    }
};

export const validateRefreshTokenMutation = async props => {
    const { createValidateTokenByRefreshMutation, dispatch } = props;
    const [refreshToken] = useAuthorityToken('refreshtoken');
    let payload = { refreshToken: refreshToken };
    if (isValidString(refreshToken)) {
        try {
            let { data, error } = await createValidateTokenByRefreshMutation({ variables: payload });
            return { data: data?.validateTokenByRefresh, error };
        } catch (error) {
            logError(error, false, 'validateRefreshToken', [props]);
            dispatch({ type: 'error', error: error.toString() });
            return { data: null, error };
        }
    } else {
        logError('refresh token not present', false, 'validateRefreshToken', [props]);
        return { data: null, error: 'refresh token not present' };
    }
};

export const getQuoteDetails = async (guid, userType) => {
    try {
        const { data, error } = await axiosInstance.get(API_CONFIG.GET_ECOM_QUOTE_URL,
            {
                headers: {
                    token: guid,
                    userType: userType === USER_TYPE.CREDIT ? 'B2B' : 'B2C'
                }
            }
        );
        return { data, error };
    } catch (error) {
        logError(error, false, 'getQuoteDetails', [guid, userType]);
        return { error };
    }
};

export const replaceCartWithQuote = async payload => {
    const { replaceCartMutation, dispatch, cartId, products, accessories } = payload;
    try {
        if (!cartId) {
            return;
        }
        const { data, error } = await replaceCartMutation({
            variables: {
                cartId,
                products,
                accessories
            },
            fetchPolicy: 'network-only'
        });
        if (error) {
            logError(error, false, 'replaceCartWithQuote', [payload]);
            return;
        }
        let estimatesData = structuredClone(data?.replaceCustomerCart?.cart);
        localStorage.setItem(STORAGE_CONFIG.LOCAL_STORAGE.CARTTOTALQUANTITY, estimatesData?.total_quantity || 0);
        generateLocalCartItems(estimatesData?.items);
        dispatch({ type: 'cart', cart: estimatesData });
        return { ...data?.replaceCustomerCart, cart: estimatesData };
    } catch (error) {
        logError(error, false, 'replaceCartWithQuote', [payload]);
        return;
    }
};
